import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { FormLayoutContent, FormLayoutFooter, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import { RootState } from 'data/root.reducers';
import * as actions from 'data/utimacoCircuitSwitch/utimacoCircuitSwitch.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as accessFunctionSelectors from 'data/accessFunction/accessFunction.selectors';
import * as collectionFunctionSelectors from 'data/collectionFunction/collectionFunction.selectors';
import * as collectionFunctionGroupSelectors from 'data/collectionFunctionGroup/collectionFunctionGroup.selectors';
import { newUtimacoCircuitSwitch } from 'routes/UtimacoCircuitSwitch/UtimacoCircuitSwitch.types';
import { adaptToCreate } from 'data/utimacoCircuitSwitch/utimacoCircuitSwitch.adapter';
import { UtimacoCircuitSwitch } from 'data/utimacoCircuitSwitch/utimacoCircuitSwitch.types';
import { FieldUpdateHandlerParam } from 'shared/form';
import { FormTemplateSection } from 'data/template/template.types';
import UtimacoCircuitSwitchForm, { Ref } from 'routes/UtimacoCircuitSwitch/components/UtimacoCircuitSwitchForm/UtimacoCircuitSwitchForm';
import { utimacoCircuitSwitchTemplate } from '../../templates';
import { adaptTemplateVisibilityMode, adaptTemplateOptions, getLeaOptions } from '../../utils/UtimacoCircuitSwitch.adapter';
import { getAuditDetailsToSubmit } from '../../utils/utimacoCircuitSwitchDataToSubmit';

type RouterParams = {
  history: string
}

const mapState = (state: RootState) => ({
  utimacoCircuitSwitchTemplateDef: adaptTemplateVisibilityMode(utimacoCircuitSwitchTemplate, 'create'),
  deliveryFunctionId: state.global.selectedDF,
  userId: state.authentication.userId,
  utimacoCircuitSwitchTemplate: adaptTemplateVisibilityMode(utimacoCircuitSwitchTemplate, 'create'),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  accessFunctions: accessFunctionSelectors.getAccessFunctions(state),
  collectionFunctions: collectionFunctionSelectors.getCollectionFunctionList(state),
  collectionFunctionGroups: collectionFunctionGroupSelectors.getCollectionFunctionGroupList(state),
});

const mapDispatch = {
  createUtimacoCircuitSwitch: actions.createUtimacoCircuitSwitch,
  goToUtimacoCircuitSwitchList: actions.goToUtimacoCircuitSwitchList,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>
export type Props = PropsFromRedux & PropsFromRouter;

export const UtimacoCircuitSwitchCreate = ({
  history,
  userId,
  deliveryFunctionId,
  userName,
  isAuditEnabled,
  utimacoCircuitSwitchTemplateDef,
  accessFunctions,
  collectionFunctions,
  collectionFunctionGroups,
  goToUtimacoCircuitSwitchList,
  createUtimacoCircuitSwitch,
}: Props): JSX.Element => {
  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<UtimacoCircuitSwitch>(cloneDeep(newUtimacoCircuitSwitch));
  const [template, setTemplate] = useState(utimacoCircuitSwitchTemplateDef);
  const { t } = useTranslation();

  useEffect(() => {
    setTemplate(
      adaptTemplateOptions(
        template,
        accessFunctions,
        collectionFunctions,
        collectionFunctionGroups,
      ),
    );
  }, [
    accessFunctions,
    collectionFunctions,
    collectionFunctionGroups,
  ]);

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    if (template == null) {
      return;
    }

    const clonedFormData = cloneDeep(formData);
    const clonedTemplate = cloneDeep(template);

    if (fieldName === 'AFID') {
      (clonedTemplate.main as FormTemplateSection).fields.LEA.options = getLeaOptions(
        value,
        accessFunctions,
      );
    }

    const newData = {
      ...clonedFormData,
      [fieldName]: value,
    };

    clonedTemplate.key = Date.now().toString();
    setFormData(newData);
    setTemplate(clonedTemplate);
  };

  const getCreateUtimacoCircuitSwitchPayload = () => {
    if (formRef.current === undefined || formRef.current === null) return null;

    if (!formRef.current.isValid(true, true)) {
      return null;
    }

    const distributorConfigFormData = formRef.current.getValues();
    const payload = {
      data: adaptToCreate(distributorConfigFormData),
      auditDetails: getAuditDetailsToSubmit(distributorConfigFormData, null, isAuditEnabled, userId, userName, 'CREATE'),
    };

    return payload;

  };

  const handleClickSave = () => {
    const payload = getCreateUtimacoCircuitSwitchPayload();
    if (payload) {
      createUtimacoCircuitSwitch(deliveryFunctionId, payload, history);
    }
  };

  const handleCancel = () => goToUtimacoCircuitSwitchList(history);

  const renderForm = () => {
    if (formData && template) {
      return (
        <UtimacoCircuitSwitchForm
          ref={formRef}
          data={formData}
          template={template}
          onFieldUpdate={handleFieldUpdate}
        />
      );
    }
  };
  const renderFormPanel = (formTitle: string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing
          onClickCancel={handleCancel}
          onClickSave={handleClickSave}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  return (
    <div data-testid="UtimacoCircuitSwitchCreate">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel(t('utimacoCircuitSwitch.create.title'))}
          {renderForm()}
        </FormLayoutContent>
        <FormLayoutFooter>
          {renderFormPanel('')}
        </FormLayoutFooter>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(UtimacoCircuitSwitchCreate));
